/**
 * Hook para gerenciar importação de Excel
 * Suporta 3 formatos: Padrão, Fintech, PORT
 */
import { useCallback } from "react";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

// Utilitário para converter datas do Excel
const converterData = (dataStr) => {
  if (!dataStr) return '';
  
  // Se for número (data serial do Excel)
  if (typeof dataStr === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + dataStr * 86400000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  const str = String(dataStr).trim();
  
  // Formato DD/MM/YYYY
  if (str.includes('/')) {
    const partes = str.split('/');
    if (partes.length === 3) {
      const dia = partes[0].padStart(2, '0');
      const mes = partes[1].padStart(2, '0');
      let ano = partes[2];
      if (ano.length === 2) {
        ano = '20' + ano;
      }
      return `${ano}-${mes}-${dia}`;
    }
  }
  
  // Formato DD-MM-YYYY
  if (str.includes('-') && str.length <= 10) {
    const partes = str.split('-');
    if (partes.length === 3 && partes[0].length <= 2) {
      const dia = partes[0].padStart(2, '0');
      const mes = partes[1].padStart(2, '0');
      let ano = partes[2];
      if (ano.length === 2) {
        ano = '20' + ano;
      }
      return `${ano}-${mes}-${dia}`;
    }
  }
  
  // Já no formato YYYY-MM-DD
  if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return str;
  }
  
  return str;
};

// Utilitário para converter números
const converterNumero = (valor) => {
  if (!valor) return 0;
  const str = String(valor).replace('%', '').replace(',', '.').trim();
  return parseFloat(str) || 0;
};

export function useExcelImport() {

  // Baixar template Excel
  const baixarTemplateExcel = useCallback(() => {
    const templateData = [
      {
        'Produto/Nome': 'EXEMPLO - CONSIGNADO 96X',
        'Prazo': '96',
        'Fator': '0.02211',
        'Taxa %': '1.85',
        'Tipo Comissão': 'Valor Líquido',
        'Vl.Ini R$': '500',
        'Vl.Fim R$': '50000',
        'Mod. Cálc.': 'V.Líquido',
        'Emp %': '9',
        'Tele %': '2',
        'Corr %': '3',
        'Aten %': '1',
        'G.Ad %': '0.5',
        'G.Op %': '0.5',
        'Bônus %': '0',
        'Dif. %': '0',
        'Início Vig.': '2025-01-01',
        'Fim Vig.': '2025-12-31'
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template Comissões');
    
    ws['!cols'] = [
      { wch: 30 }, { wch: 8 }, { wch: 10 }, { wch: 8 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
      { wch: 12 }, { wch: 12 }
    ];
    
    XLSX.writeFile(wb, 'template_comissoes_stilocred.xlsx');
    toast.success("Template baixado com sucesso!");
  }, []);

  // Importar formato padrão
  const importarExcelPadrao = useCallback((file, tipoOperacao) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            toast.error("Arquivo vazio ou formato inválido");
            reject(new Error("Arquivo vazio"));
            return;
          }
          
          const novasComissoes = jsonData.map((row, index) => ({
            id: String(Date.now() + index),
            produto: row['Produto/Nome'] || '',
            prazo: String(row['Prazo'] || ''),
            fator: row['Fator'] || null,
            taxa: row['Taxa %'] || null,
            tipoComissao: row['Tipo Comissão'] === 'Valor Bruto' ? 'Valor Bruto' : 'Valor Líquido',
            valorInicial: row['Vl.Ini R$'] || null,
            valorFinal: row['Vl.Fim R$'] || null,
            modeloCalculo: row['Mod. Cálc.'] === 'Com.Emp' ? 'COMISSAO_EMPRESA' : 'VALOR_CONTRATO',
            empresa: Number(row['Emp %']) || 0,
            teleVenda: Number(row['Tele %']) || 0,
            corretor: Number(row['Corr %']) || 0,
            atendente: Number(row['Aten %']) || 0,
            gerAdm: Number(row['G.Ad %']) || 0,
            gerOper: Number(row['G.Op %']) || 0,
            bonus: Number(row['Bônus %']) || 0,
            diferido: Number(row['Dif. %']) || 0,
            dataInicioVigencia: row['Início Vig.'] || '',
            dataFimVigencia: row['Fim Vig.'] || '',
            tipo: tipoOperacao || ''
          }));
          
          toast.success(`${novasComissoes.length} linha(s) importada(s) com sucesso!`);
          resolve(novasComissoes);
        } catch (error) {
          console.error("Erro ao importar:", error);
          toast.error("Erro ao processar arquivo Excel");
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  // Importar formato Fintech
  const importarExcelFintech = useCallback((file, tipoOperacao) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            toast.error("Arquivo vazio ou formato inválido");
            reject(new Error("Arquivo vazio"));
            return;
          }
          
          const novasComissoes = jsonData.map((row, index) => {
            const nomeTabela = row['Nome Tabela'] || row['Nome_Tabela'] || row['NomeTabela'] || '';
            const tipoComissao = row['Tipo'] || row['Tipo Comissão'] || '';
            const comissao = row['Comissão'] || row['Comissao'] || 0;
            const valorInicial = row['Valor Inicial'] || row['Valor_Inicial'] || row['ValorInicial'] || null;
            const valorFinal = row['Valor Final'] || row['Valor_Final'] || row['ValorFinal'] || null;
            const prazoMaximo = row['Prazo Máximo'] || row['Prazo_Maximo'] || row['PrazoMaximo'] || row['Prazo Maximo'] || '';
            const dataInicio = row['Data Vigência Inicial'] || row['Data_Vigencia_Inicial'] || row['DataVigenciaInicial'] || row['Data Vigencia Inicial'] || '';
            const dataFim = row['Data Vigência Final'] || row['Data_Vigencia_Final'] || row['DataVigenciaFinal'] || row['Data Vigencia Final'] || '';
            
            let tipoComissaoConvertido = 'Valor Líquido';
            if (tipoComissao && (tipoComissao.toLowerCase().includes('bruto') || tipoComissao === 'Valor Bruto')) {
              tipoComissaoConvertido = 'Valor Bruto';
            }
            
            let empresaPerc = 0;
            if (comissao) {
              const comStr = String(comissao).replace('%', '').replace(',', '.').trim();
              empresaPerc = parseFloat(comStr) || 0;
            }
            
            return {
              id: String(Date.now() + index),
              produto: nomeTabela,
              prazo: String(prazoMaximo || ''),
              fator: null,
              taxa: null,
              tipoComissao: tipoComissaoConvertido,
              valorInicial: valorInicial,
              valorFinal: valorFinal,
              modeloCalculo: 'COMISSAO_EMPRESA',
              empresa: empresaPerc,
              teleVenda: 0,
              corretor: 30,
              atendente: 5,
              gerAdm: 10,
              gerOper: 0,
              bonus: 0,
              diferido: 0,
              dataInicioVigencia: converterData(dataInicio),
              dataFimVigencia: converterData(dataFim),
              tipo: tipoOperacao || ''
            };
          });
          
          const comissoesFiltradas = novasComissoes.filter(c => c.produto && c.produto.trim() !== '');
          
          if (comissoesFiltradas.length === 0) {
            toast.error("Nenhum dado válido encontrado no arquivo");
            reject(new Error("Nenhum dado válido"));
            return;
          }
          
          toast.success(`${comissoesFiltradas.length} linha(s) importada(s) do Fintech com sucesso!`);
          resolve(comissoesFiltradas);
        } catch (error) {
          console.error("Erro ao importar Fintech:", error);
          toast.error("Erro ao processar arquivo do Fintech");
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);

  // Importar formato PORT
  const importarExcelPort = useCallback((file, tipoOperacao) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            toast.error("Arquivo vazio ou formato inválido");
            reject(new Error("Arquivo vazio"));
            return;
          }
          
          if (jsonData.length > 0) {
            console.log("PORT Import - Colunas disponíveis:", Object.keys(jsonData[0]));
          }
          
          const novasComissoes = jsonData.map((row, index) => {
            const tabela = row['Tabela'] || row['TABELA'] || row['tabela'] || '';
            const valorDe = row['Valor de'] || row['Valor De'] || row['ValorDe'] || row['VALOR DE'] || row['Valor_de'] || null;
            const valorA = row['Valor a'] || row['Valor A'] || row['ValorA'] || row['VALOR A'] || row['Valor_a'] || null;
            const prazoA = row['Prazo A'] || row['Prazo a'] || row['PrazoA'] || row['PRAZO A'] || row['Prazo_A'] || '';
            const comissao = row['Comissão'] || row['Comissao'] || row['ComissÃ£o'] || row['COMISSÃO'] || row['COMISSAO'] || 0;
            const bonus = row['Bônus'] || row['Bonus'] || row['BÃ´nus'] || row['BÔNUS'] || row['BONUS'] || 0;
            const diferido = row['Diferido'] || row['DIFERIDO'] || 0;
            
            const dtInicial = row['Dt. Inicial'] || row['Dt Inicial'] || row['DtInicial'] || 
                             row['Dt.Inicial'] || row['DT. INICIAL'] || row['DT INICIAL'] ||
                             row['Data Inicial'] || row['DataInicial'] || row['DATA INICIAL'] ||
                             row['Dt. inicial'] || row['dt. inicial'] || row['dt inicial'] ||
                             row['Início'] || row['Inicio'] || row['INÍCIO'] || row['INICIO'] || '';
            const atualizadoEm = row['Atualizado em'] || row['Atualizado Em'] || row['AtualizadoEm'] || 
                                 row['ATUALIZADO EM'] || row['Atualizado_em'] || row['Data Atualização'] ||
                                 row['Dt. Final'] || row['Dt Final'] || row['DT. FINAL'] || row['DT FINAL'] ||
                                 row['Atualizado'] || row['ATUALIZADO'] || row['atualizado em'] ||
                                 row['Fim'] || row['FIM'] || row['Data Final'] || row['DATA FINAL'] || '';
            
            return {
              id: String(Date.now() + index),
              produto: tabela,
              prazo: String(prazoA || ''),
              fator: null,
              taxa: null,
              tipoComissao: 'Valor Líquido',
              valorInicial: valorDe,
              valorFinal: valorA,
              modeloCalculo: 'COMISSAO_EMPRESA',
              empresa: converterNumero(comissao),
              teleVenda: 0,
              corretor: 30,
              atendente: 5,
              gerAdm: 10,
              gerOper: 0,
              bonus: converterNumero(bonus),
              diferido: converterNumero(diferido),
              dataInicioVigencia: converterData(dtInicial),
              dataFimVigencia: converterData(atualizadoEm),
              tipo: tipoOperacao || ''
            };
          });
          
          const comissoesFiltradas = novasComissoes.filter(c => c.produto && c.produto.trim() !== '');
          
          if (comissoesFiltradas.length === 0) {
            toast.error("Nenhum dado válido encontrado no arquivo");
            reject(new Error("Nenhum dado válido"));
            return;
          }
          
          toast.success(`${comissoesFiltradas.length} linha(s) importada(s) do PORT com sucesso!`);
          resolve(comissoesFiltradas);
        } catch (error) {
          console.error("Erro ao importar PORT:", error);
          toast.error("Erro ao processar arquivo do PORT");
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);


  const importarExcelLev = useCallback((file, tipoOperacao) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            toast.error("Arquivo vazio ou formato invalido");
            reject(new Error("Arquivo vazio"));
            return;
          }
          
          if (jsonData.length > 0) {
            console.log("LEV Import - Colunas disponiveis:", Object.keys(jsonData[0]));
          }
          
          const novasComissoes = jsonData.map((row, index) => {
            const tabela = row["Tabela"] || row["TABELA"] || row["tabela"] || row["Produto"] || row["PRODUTO"] || "";
            const valorDe = row["Valor de"] || row["Valor De"] || row["ValorDe"] || row["VALOR DE"] || row["De"] || row["Vl Inicial"] || row["VL INICIAL"] || null;
            const valorA = row["Valor a"] || row["Valor A"] || row["ValorA"] || row["VALOR A"] || row["Ate"] || row["Vl Final"] || row["VL FINAL"] || null;
            const prazo = row["Prazo"] || row["PRAZO"] || row["Prazo A"] || row["Prazo a"] || "";
            const comissao = row["Comissao"] || row["Comiss\u00e3o"] || row["COMISSAO"] || row["COMISS\u00c3O"] || row["Emp %"] || 0;
            const bonus = row["Bonus"] || row["B\u00f4nus"] || row["BONUS"] || row["B\u00d4NUS"] || 0;
            const diferido = row["Diferido"] || row["DIFERIDO"] || row["Dif"] || row["DIF"] || 0;
            
            const dtInicial = row["Dt. Inicial"] || row["Dt Inicial"] || row["DT. INICIAL"] ||
                             row["Data Inicial"] || row["DATA INICIAL"] || row["Inicio"] || row["INICIO"] || "";
            const dtFinal = row["Dt. Final"] || row["Dt Final"] || row["DT. FINAL"] ||
                           row["Data Final"] || row["DATA FINAL"] || row["Fim"] || row["FIM"] ||
                           row["Atualizado em"] || row["Atualizado Em"] || "";
            
            return {
              id: String(Date.now() + index),
              produto: tabela,
              prazo: String(prazo || ""),
              fator: null,
              taxa: null,
              tipoComissao: "Valor Liquido",
              valorInicial: valorDe,
              valorFinal: valorA,
              modeloCalculo: "COMISSAO_EMPRESA",
              empresa: converterNumero(comissao),
              teleVenda: 0,
              corretor: 30,
              atendente: 5,
              gerAdm: 10,
              gerOper: 0,
              bonus: converterNumero(bonus),
              diferido: converterNumero(diferido),
              dataInicioVigencia: converterData(dtInicial),
              dataFimVigencia: converterData(dtFinal),
              tipo: tipoOperacao || ""
            };
          });
          
          const comissoesFiltradas = novasComissoes.filter(c => c.produto && c.produto.trim() !== "");
          
          if (comissoesFiltradas.length === 0) {
            toast.error("Nenhum dado valido encontrado no arquivo");
            reject(new Error("Nenhum dado valido"));
            return;
          }
          
          toast.success(comissoesFiltradas.length + " linha(s) importada(s) do LEV com sucesso!");
          resolve(comissoesFiltradas);
        } catch (error) {
          console.error("Erro ao importar LEV:", error);
          toast.error("Erro ao processar arquivo do LEV");
          reject(error);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }, []);


  return {
    baixarTemplateExcel,
    importarExcelPadrao,
    importarExcelFintech,
    importarExcelPort,
    importarExcelLev
  };
}
